/**
 * 課題管理の「やること」の画面の並べ方（済みにしたカードを開き直すまで残す・絞り込み・次のカード・残りの数）。
 *
 * カードの一覧と並び順は API が決める。画面は、この画面を開いている間に済みにしたカードを取り消し線で元の位置に残し
 * （サーバの一覧からは消えても）、「元に戻す」を押せるようにする。
 *
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business やることの画面で、済みにしたカードを元の位置に残し、種類と自分の分で絞り、押した後に次の済みでないカードを選び、残りの数を数える
 * @graph-connects none
 */
import type { TodoCard, TodoCardType } from '@cortex/domain-ac-mens-hub-api';

/**
 * 済みにしたカード（済みにした時点のカード・結果の1行・元に戻す操作・一覧の何番目にあったか）。
 * @graph-connects none
 */
export interface DoneCard {
  card: TodoCard;
  label: string;
  operationId: string | null;
  index: number;
}

/**
 * やることの絞り込み（すべて・振り分け・判定・確認）。
 * @graph-connects none
 */
export type TodoFilter = 'all' | TodoCardType;

/**
 * 一覧に出す1行（カードと、済みならその記録）。
 * @graph-connects none
 */
export interface TodoRow {
  card: TodoCard;
  done: DoneCard | null;
}

/**
 * サーバのカードに、済みにしたカードを元の位置で差し込む。済みの記録があるカードは、サーバに残っていても済みとして出す
 * （「Backlogで確かめた」で次の同期まで残る案件など）。
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business やることの一覧に、この画面で済みにしたカードを済みにした時の位置で取り消し線つきで残す
 * @graph-connects none
 */
export function mergeDoneCards(
  cards: readonly TodoCard[],
  done: ReadonlyMap<string, DoneCard>,
): TodoRow[] {
  const rows: TodoRow[] = cards.map((card) => ({ card, done: done.get(card.cardId) ?? null }));
  const present = new Set(cards.map((card) => card.cardId));
  const missing = [...done.values()]
    .filter((entry) => !present.has(entry.card.cardId))
    .sort((a, b) => a.index - b.index);
  for (const entry of missing) {
    rows.splice(Math.min(entry.index, rows.length), 0, { card: entry.card, done: entry });
  }
  return rows;
}

/**
 * 絞り込み（種類・自分の分だけ）を当てた行。
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business やることの一覧を、カードの種類と自分が受け持つカードだけかで絞る
 * @graph-connects none
 */
export function filterTodoRows(
  rows: readonly TodoRow[],
  filter: TodoFilter,
  mine: boolean,
): TodoRow[] {
  return rows.filter(
    (row) => (filter === 'all' || row.card.type === filter) && (!mine || row.card.mine),
  );
}

/**
 * 済みでない行の数を、種類ごとと合計で数える（自分の分だけなら自分の分だけ）。
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business やることの絞り込みに添える、残りのカードの数を種類ごとと合計で数える
 * @graph-connects none
 */
export function countLeft(rows: readonly TodoRow[], mine: boolean): Record<TodoFilter, number> {
  const counts: Record<TodoFilter, number> = { all: 0, triage: 0, judge: 0, check: 0 };
  for (const row of rows) {
    if (row.done !== null || (mine && !row.card.mine)) continue;
    counts.all += 1;
    counts[row.card.type] += 1;
  }
  return counts;
}

/**
 * 今のカードの次の、済みでないカード（末尾まで行ったら先頭へ戻る）。無ければ null。
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business やることでカードを押した後に開く、次の済みでないカードを選ぶ
 * @graph-connects none
 */
export function nextOpenCardId(rows: readonly TodoRow[], currentId: string | null): string | null {
  const start = rows.findIndex((row) => row.card.cardId === currentId);
  for (let step = 1; step <= rows.length; step += 1) {
    const row = rows[(start + step + rows.length) % rows.length];
    if (row !== undefined && row.done === null) return row.card.cardId;
  }
  return null;
}
